<script setup>
/**
 * 낭만월운 카드 컨텐츠 드로어 (PC 전용)
 *
 * - 화면 오른쪽에서 슬라이드 인
 * - card.drawer 값에 따라 전용 레이아웃 렌더 (letter / energy)
 * - 그 외에는 placeholder 노출 (향후 카드 종류별 컴포넌트로 교체 예정)
 */
import { addContentLogAPI } from '@/api/logServiceApi';
import mailboxImg from '@manseImages/monthly-fortune/letter-mailbox.png';
import doveImg from '@manseImages/monthly-fortune/letter-dove.png';
import batteryImg from '@manseImages/monthly-fortune/energy-battery.png';
import luckyCardsImg from '@manseImages/monthly-fortune/lucky-cards.png';
import luckyCloverImg from '@manseImages/monthly-fortune/lucky-clover.png';
import romanceHeartImg from '@manseImages/monthly-fortune/romance-heart.png';
import romanceBowImg from '@manseImages/monthly-fortune/romance-bow.png';
import healthClipboardImg from '@manseImages/monthly-fortune/health-clipboard.png';
import romanceStyleHeartImg from '@manseImages/monthly-fortune/romance-style-heart.png';
import moneyCoinsImg from '@manseImages/monthly-fortune/money-coins.png';
import capabilityTrophyImg from '@manseImages/monthly-fortune/capability-trophy.png';
import cautionWarningImg from '@manseImages/monthly-fortune/caution-warning.png';
import modeMirrorImg from '@manseImages/monthly-fortune/mode-mirror.png';
import modeArrowDownImg from '@manseImages/monthly-fortune/icon-mode-arrow-down.svg?url';

/** 응답 데이터 내부의 줄바꿈(`\n`) 을 `<br>` 로 변환해 v-html 로 렌더링하기 위한 헬퍼.
 *  - `transformMonthlyFortuneData` 가 `|` 를 `\n` 으로 정규화해 둔 상태이므로 여기서 `\n → <br>` 만 처리한다. */
const nl2br = (s) => (s == null ? '' : String(s).replaceAll('\n', '<br>'));

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  card: { type: Object, default: null },
  /** suggestionFlag=1 무료데이터 (전체 7개 섹션) */
  freeData: { type: Object, default: null },
  /** suggestionFlag=3 유료데이터 (12개 섹션, 구매한 경우만) */
  paidData: { type: Object, default: null },
  selectedMonth: { type: Number, default: null },
  selectedYear: { type: Number, default: null },
  userName: { type: String, default: '' },
});

// =============== 무료 섹션별 데이터 ===============
const letterFree = computed(() => props.freeData?.['1_낭만편지'] || null);
const energyFree = computed(() => props.freeData?.['2_에너지활성도'] || null);
const luckyFree = computed(() => props.freeData?.['3_행운카드'] || null);
const romanceFree = computed(() => props.freeData?.['4_연애감정'] || null);
const healthFree = computed(() => props.freeData?.['5_건강'] || null);

/** 편지본문을 줄 단위로 분리 */
const letterLines = computed(() => {
  const body = letterFree.value?.편지본문 || '';
  return body.split('\n').map((s) => s.trim()).filter(Boolean);
});

/** 에너지 활성도 게이지 (무료데이터 우선, 없으면 placeholder) */
const energyPercentDynamic = computed(() => energyFree.value?.활성도 ?? null);
const energyTrend = computed(() => energyFree.value?.추이 || null);

/** 에너지 카테고리 가이드 4개 */
const energyGuide = computed(() => {
  const g = energyFree.value?.가이드 || {};
  return {
    love: g.연애 || null,
    work: g.업무 || null,
    money: g.재물 || null,
    health: g.건강 || null,
  };
});

/** 행운카드 항목을 ·로 split → trim/filter */
const splitByDot = (str) =>
  (str || '').split(/[·,]/).map((s) => s.trim()).filter(Boolean);

/** N개씩 묶어 줄별로 ·로 join */
const chunkJoinByDot = (items, size) => {
  const lines = [];
  for (let i = 0; i < items.length; i += size) {
    lines.push(items.slice(i, i + size).join(' · '));
  }
  return lines;
};

/** 균형 컬러: 첫 줄 1개, 둘째 줄부터 2개씩 */
const luckyColorLines = computed(() => {
  const items = splitByDot(luckyFree.value?.균형컬러);
  if (!items.length) return [];
  return [items[0], ...chunkJoinByDot(items.slice(1), 2)];
});
/** 이달의 식탁: 2개씩 */
const luckyFoodLines = computed(() =>
  chunkJoinByDot(splitByDot(luckyFree.value?.이달의식탁), 2),
);
/** 에너지 충전: 2개씩 */
const luckyEnergyLines = computed(() =>
  chunkJoinByDot(splitByDot(luckyFree.value?.에너지충전), 2),
);
/** 행운 날짜: 2개씩 묶어 줄별로 표시 */
const luckyDayLines = computed(() => {
  const arr = luckyFree.value?.행운일;
  if (!Array.isArray(arr) || !arr.length) return [];
  const items = arr.map((d) => `${d}일`);
  return chunkJoinByDot(items, 2);
});
const luckyNumbers = computed(() => {
  const arr = luckyFree.value?.균형숫자;
  if (Array.isArray(arr) && arr.length) return arr.join(' · ');
  return '';
});

// =============== 유료 섹션별 데이터 ===============
const romanceStylePaid = computed(() => props.paidData?.['7_연애스타일'] || null);
const moneyPaid = computed(() => props.paidData?.['8_돈관리'] || null);
const capabilityPaid = computed(() => props.paidData?.['9_역량'] || null);
const cautionPaid = computed(() => props.paidData?.['10_체크포인트'] || null);
const modePaid = computed(() => props.paidData?.['11_낭만성향'] || null);

// =============== 연애 스타일(romanceStyle) ===============
/** 연애 스타일 이름 → 이모지 매핑 (없으면 기본 💝) */
const ROMANCE_STYLE_EMOJI = {
  행동형: '💪',
  감성형: '🌙',
  신중형: '🤔',
  이성형: '🧠',
  적극형: '🚀',
  소극형: '🌱',
  열정형: '🔥',
  안정형: '🌿',
  주도형: '🎯',
  관계형: '🤝',
  자유형: '🕊️',
  로맨틱형: '💕',
};
const styleEmojiOf = (label) => ROMANCE_STYLE_EMOJI[label] || '';

/** 문자열을 줄/문장 단위로 분리. \n 우선, 없으면 ". "·" / " 기준. */
const splitToLines = (str) => {
  if (!str) return [];
  if (str.includes('\n')) return str.split('\n').map((s) => s.trim()).filter(Boolean);
  return [str.trim()];
};

const romanceStyleShift = computed(() => {
  const sc = romanceStylePaid.value?.스타일변화;
  return {
    from: { emoji: styleEmojiOf(sc?.이전), label: sc?.이전 || '' },
    to: { emoji: styleEmojiOf(sc?.이후), label: sc?.이후 || '' },
    desc: splitToLines(sc?.설명),
  };
});

const mapDayEntries = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return [];
  return arr.map((it) => ({
    day: `${it.날짜}일`,
    lines: splitToLines(it.설명).length ? splitToLines(it.설명) : [it.설명 || ''],
  }));
};

const romanceStyleGoodDays = computed(() => mapDayEntries(romanceStylePaid.value?.좋은날));
const romanceStyleWarnDays = computed(() => mapDayEntries(romanceStylePaid.value?.과한날));

const romanceStyleTipLines = computed(() => splitToLines(romanceStylePaid.value?.과한날대처));
const romanceStyleTipExtra = computed(() => '');

const romanceStyleMoodBadge = computed(() => romanceStylePaid.value?.필요한감성 || '');
const romanceStyleMoodDesc = computed(() => splitToLines(romanceStylePaid.value?.감성설명));

const romanceStyleMoodAdvices = computed(() => {
  const c = romanceStylePaid.value?.채우는법;
  if (!c) return [];
  return [
    { title: '💑 파트너가 있다면', text: c.파트너 },
    { title: '🙋 새로운 만남에서는', text: c.새만남 },
    { title: '🧘 혼자 채우고 싶다면', text: c.혼자 },
  ].map((it) => ({ title: it.title, texts: splitToLines(it.text) }));
});

// =============== 돈관리(money) ===============
/** 재물 방향 → 이모지 매핑 (없으면 기본 💰) */
const MONEY_DIRECTION_EMOJI = {
  적극형: '🚀',
  유지형: '🛡️',
  보수형: '🐢',
  안정형: '🌿',
  도전형: '⚡',
  절약형: '💼',
  계획형: '📊',
};
const moneyDirectionEmoji = (label) => MONEY_DIRECTION_EMOJI[label] || '';

const moneyShift = computed(() => {
  const m = moneyPaid.value;
  return {
    label: m?.재물방향 || '',
    emoji: moneyDirectionEmoji(m?.재물방향),
    headline: m?.핵심메시지 || '',
    desc: splitToLines(m?.재물방향설명),
  };
});

const moneyDangerDays = computed(() => mapDayEntries(moneyPaid.value?.후회할날));
const moneyThriftyDays = computed(() => mapDayEntries(moneyPaid.value?.알뜰날));

const moneyTipLines = computed(() => splitToLines(moneyPaid.value?.후회할날대처));
const moneyTipExtra = computed(() => '');

const moneyPeriodGuides = computed(() => {
  const p = moneyPaid.value?.시기별;
  if (!p) return [];
  return ['전반', '중반', '후반']
    .map((key) => ({ key, lines: splitToLines(p[key]) }))
    .filter((it) => it.lines.length);
});

const moneyMonthlyTips = computed(() => {
  const tips = moneyPaid.value?.재물팁;
  return Array.isArray(tips) ? tips : [];
});

// =============== 체크포인트(caution) ===============
/** 등급(또는 상황 유형) → 이모지 매핑. 없으면 🟢 */
const CAUTION_GRADE_EMOJI = {
  무난: '🟢',
  보통: '🟢',
  순함: '🟢',
  주의: '🟡',
  경고: '🔴',
  위험: '🔴',
};
const cautionGradeEmoji = (label) => CAUTION_GRADE_EMOJI[label] || '';

/** 상황 유형에 따른 chip 클래스 색상 키 */
const CAUTION_CHIP_COLOR = {
  무난: 'green',
  보통: 'green',
  순함: 'green',
  주의: 'yellow',
  경고: 'red',
  위험: 'red',
};
const cautionChipColor = (label) => CAUTION_CHIP_COLOR[label] || 'green';

const cautionGrade = computed(() => cautionPaid.value?.등급 || '');
const cautionLead = computed(() => cautionPaid.value?.총평 || '');

const cautionSituations = computed(() => {
  const list = cautionPaid.value?.상황;
  if (!Array.isArray(list) || !list.length) return [];
  return list.map((s) => ({
    유형: s.유형 || '',
    제목: s.제목 || '',
    상황lines: splitToLines(s.상황),
    대처lines: Array.isArray(s.대처) ? s.대처.flatMap((d) => splitToLines(d)) : splitToLines(s.대처),
  }));
});

const cautionLifestyle = computed(() => splitToLines(cautionPaid.value?.생활체크));

const cautionCheckDays = computed(() => mapDayEntries(cautionPaid.value?.체크날짜));

/** 이전/이번/다음 월 라벨 */
const prevMonthLabel = computed(() => {
  if (!props.selectedYear || !props.selectedMonth) return '';
  const d = new Date(props.selectedYear, props.selectedMonth - 2, 1);
  return `${d.getMonth() + 1}월`;
});
const currMonthLabel = computed(() =>
  props.selectedMonth ? `${props.selectedMonth}월` : '',
);
const nextMonthLabel = computed(() => {
  if (!props.selectedYear || !props.selectedMonth) return '';
  const d = new Date(props.selectedYear, props.selectedMonth, 1);
  return `${d.getMonth() + 1}월`;
});

const emit = defineEmits(['update:modelValue', 'close']);

const isOpen = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

usePCScrollLock(isOpen);

const isLetter = computed(() => props.card?.drawer === 'letter');
const isEnergy = computed(() => props.card?.drawer === 'energy');
const isLucky = computed(() => props.card?.drawer === 'lucky');
const isRomance = computed(() => props.card?.drawer === 'romance');
const isHealth = computed(() => props.card?.drawer === 'health');
const isRomanceStyle = computed(() => props.card?.drawer === 'romanceStyle');
const isMoney = computed(() => props.card?.drawer === 'money');
const isCapability = computed(() => props.card?.drawer === 'capability');
const isCaution = computed(() => props.card?.drawer === 'caution');
const isMode = computed(() => props.card?.drawer === 'mode');
const isDetail = computed(() => (
  isLetter.value
    || isEnergy.value
    || isLucky.value
    || isRomance.value
    || isHealth.value
    || isRomanceStyle.value
    || isMoney.value
    || isCapability.value
    || isCaution.value
    || isMode.value
));

// MBTI 색상 (이달 행만 적용, 나는 행은 회색 #949494)
const MBTI_COLOR = {
  E: '#ff6867',
  I: '#5cb8e6',
  N: '#adcc32',
  S: '#7c6ad4',
  F: '#de3a97',
  T: '#2dbb95',
  P: '#f3ae11',
  J: '#4780cc',
};

/** 낭만성향(11_) 축별 데이터를 modeAxes 형태로 매핑 */
const MODE_AXIS_KEYS = [
  { key: 'EI', pair: 'E / I' },
  { key: 'NS', pair: 'N / S' },
  { key: 'FT', pair: 'F / T' },
  { key: 'PJ', pair: 'P / J' },
];

const modeAxes = computed(() => {
  const byAxis = modePaid.value?.축별;
  if (!byAxis) return [];
  return MODE_AXIS_KEYS.map(({ key, pair }) => {
    const ax = byAxis[key];
    if (!ax) return null;
    const desc = [ax.설명, ax.좋은면, ax.조심할면].filter(Boolean);
    return {
      pair,
      me: { letter: ax.근원 || '', value: Math.round(ax.근원수치 ?? 0) },
      month: { letter: ax.이달 || '', value: Math.round(ax.이달수치 ?? 0) },
      state: ax.변화 ? '변화' : '평소와 비슷',
      desc,
      ...(ax.팁 ? { tip: ax.팁 } : {}),
    };
  }).filter(Boolean);
});

const mbtiColor = (letter) => MBTI_COLOR[letter] || '#949494';

const CAPABILITY_COLORS = {
  추진력: '#555de6',
  기획력: '#f3ae11',
  설득력: '#e67755',
};

const capabilityStats = computed(() => {
  const bars = capabilityPaid.value?.역량바;
  if (!bars) return [];
  return ['추진력', '기획력', '설득력'].map((label) => ({
    label,
    value: bars[label]?.수치 ?? 0,
    color: CAPABILITY_COLORS[label],
  }));
});

/** 업무 카드: chip(라벨 + 수치%) + 가이드 본문 줄 */
const capabilityWorkCards = computed(() => {
  const bars = capabilityPaid.value?.역량바;
  if (!bars) return [];
  return ['추진력', '기획력', '설득력'].map((label) => {
    const item = bars[label] || {};
    return {
      label,
      value: item.수치 ?? 0,
      color: CAPABILITY_COLORS[label],
      lines: splitToLines(item.가이드),
    };
  });
});

/** 업무 키워드 (· 구분) */
const capabilityKeywords = computed(() => {
  const kw = capabilityPaid.value?.업무키워드;
  if (!kw) return [];
  return kw
    .split(/[·,]/)
    .map((s) => s.trim())
    .filter(Boolean);
});

/** 관계 영역 — intro/스타일/팁 */
const capabilityRelation = computed(() => {
  const rel = capabilityPaid.value?.관계;
  return {
    introLines: splitToLines(rel?.가이드),
    style: rel?.스타일 || '',
    tipLines: splitToLines(rel?.팁),
  };
});

const capabilitySummary = computed(() => capabilityPaid.value?.종합 || '');

// =============== 모드(mode) ===============
const modeTitle = computed(() => {
  const c = modePaid.value?.캐릭터;
  return c ? `✨ ${c}` : '';
});

const modeMbtiTransition = computed(() => {
  const from = modePaid.value?.근원NCODE;
  const to = modePaid.value?.이달NCODE;
  if (from && to) return `${from} → ${to}`;
  return '';
});

const modeLevelText = computed(() => {
  const bar = modePaid.value?.변화폭 || '';
  const desc = modePaid.value?.변화설명 || '';
  return [bar, desc].filter(Boolean).join(' ');
});

const modeLevelBar = computed(() => modePaid.value?.변화폭 || '');
const modeLevelDesc = computed(() => modePaid.value?.변화설명 || '');

const modeDescText = computed(() => modePaid.value?.종합 || '');

const modeIntroLines = computed(() => splitToLines(modePaid.value?.인트로));

/** "요즘 X" — 설명 형태의 주변반응 문자열을 bubble + desc 로 분리 */
const parseReaction = (str) => {
  if (!str) return { bubble: '', desc: '' };
  // " 또는 " 따옴표 안의 텍스트를 bubble, 그 뒤(— 이후)를 desc
  const m = str.match(/[""]([^""]+)[""]\s*[—–-]\s*(.+)/);
  if (m) return { bubble: m[1].trim(), desc: m[2].trim() };
  const m2 = str.match(/[""]([^""]+)[""]\s*(.*)/);
  if (m2) return { bubble: m2[1].trim(), desc: m2[2].trim() };
  return { bubble: '', desc: str.trim() };
};

const MODE_REACTION_LABELS = {
  파트너: '💑 파트너',
  동료: '👥 동료',
  친구: '🤝 친구',
};
const modeReactions = computed(() => {
  const rx = modePaid.value?.주변반응 || {};
  return ['파트너', '동료', '친구']
    .map((key) => {
      const parsed = parseReaction(rx[key]);
      if (!parsed.bubble && !parsed.desc) return null;
      return {
        who: MODE_REACTION_LABELS[key],
        bubble: parsed.bubble,
        desc: parsed.desc,
      };
    })
    .filter(Boolean);
});

const MODE_TIME_LABELS = { 아침: '🌅 아침', 낮: '☕ 낮', 저녁: '🌙 저녁' };

const modeLifestyle = computed(() => {
  const ls = modePaid.value?.생활습관 || {};
  return ['아침', '낮', '저녁']
    .map((key) => {
      const lines = splitToLines(ls[key]);
      if (!lines.length) return null;
      return {
        head: MODE_TIME_LABELS[key],
        lines,
      };
    })
    .filter(Boolean);
});

const modeNextMonthLines = computed(() => splitToLines(modePaid.value?.다음달예고));

const energyPercent = computed(() => energyPercentDynamic.value ?? 0);

const handleClose = () => {
  addContentLogAPI({
    contentType: props.card?.isPaid
      ? 'am_monthly_drawer_close_paid_opt3'
      : 'am_monthly_drawer_close_free_opt3',
    option3: props.card?.key || '',
  });
  isOpen.value = false;
  emit('close');
};

const onKeydown = (e) => {
  if (e.key === 'Escape' && isOpen.value) handleClose();
};
onMounted(() => window.addEventListener('keydown', onKeydown));
onUnmounted(() => window.removeEventListener('keydown', onKeydown));
</script>

<template>
  <Teleport to="body">
    <Transition name="mf-drawer">
      <div v-if="isOpen" class="mf-drawer-backdrop" @click.self="handleClose">
        <aside
          class="mf-drawer"
          :class="{ 'mf-drawer--detail': isDetail }"
          role="dialog"
          aria-modal="true"
        >
            <!-- 낭만 편지 전용 레이아웃 -->
            <template v-if="isLetter">
              <header class="mf-letter__header">
                <button
                  type="button"
                  class="mf-letter__close"
                  aria-label="닫기"
                  @click="handleClose"
                >
                  <span class="mf-letter__close-icon"></span>
                </button>
              </header>

              <div class="mf-letter__body">
                <!-- 좌측: 표지 -->
                <section class="mf-letter__cover">
                  <div class="mf-letter__cover-inner">
                  <h3 class="mf-letter__title">낭만 편지</h3>
                  <img
                    :src="mailboxImg"
                    alt="낭만 편지 우체통"
                    class="mf-letter__mailbox"
                  />
                  <div class="mf-letter__cover-text">
                    <p class="mf-letter__lead">이 달의 편지가 도착했어요</p>
                    <p class="mf-letter__sub">
                      하루하루의 노력 속에 만들어지는<br />
                      당신의 소중한 한 달을 응원합니다
                    </p>
                  </div>
                  </div>
                </section>

                <div class="mf-letter__divider" aria-hidden="true"></div>

                <!-- 우측: 컨텐츠 -->
                <section class="mf-letter__content">
                  <img :src="doveImg" alt="" class="mf-letter__dove" />

                  <div class="mf-letter__quote">
                    <svg class="mf-letter__quote-mark mf-letter__quote-mark--open" width="18" height="13.5" viewBox="0 0 18 13.5" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5.625 2.25L6.75 0H4.5C2.01375 0 0 3.13875 0 5.625V13.5H7.875V5.625H3.375C3.375 2.25 5.625 2.25 5.625 2.25ZM13.5 5.625C13.5 2.25 15.75 2.25 15.75 2.25L16.875 0H14.625C12.1387 0 10.125 3.13875 10.125 5.625V13.5H18V5.625H13.5Z" fill="#529937" />
                    </svg>
                    <p class="mf-letter__quote-text">
                      {{ letterFree?.이달의나 || '' }}
                    </p>
                    <svg class="mf-letter__quote-mark mf-letter__quote-mark--close" width="18" height="13.5" viewBox="0 0 18 13.5" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5.625 2.25L6.75 0H4.5C2.01375 0 0 3.13875 0 5.625V13.5H7.875V5.625H3.375C3.375 2.25 5.625 2.25 5.625 2.25ZM13.5 5.625C13.5 2.25 15.75 2.25 15.75 2.25L16.875 0H14.625C12.1387 0 10.125 3.13875 10.125 5.625V13.5H18V5.625H13.5Z" fill="#529937" />
                    </svg>
                  </div>

                  <article class="mf-letter__card">
                    <div class="mf-letter__card-inner">
                      <header class="mf-letter__card-title">
                        <p>
                          <span class="mf-letter__card-title-prefix">
                            <span>To.&nbsp;</span>
                            <span class="mf-letter__accent">{{ selectedMonth || '-' }}월</span>
                            <span>의&nbsp;</span>
                          </span>
                          <span class="mf-letter__card-title-user">{{ userName || '-' }}</span>
                          <span class="mf-letter__card-title-suffix">&nbsp;님에게 🍀</span>
                        </p>
                      </header>
                      <ul v-if="letterLines.length" class="mf-letter__lines">
                        <li v-for="(line, i) in letterLines" :key="i" v-html="line"></li>
                      </ul>
                    </div>
                  </article>

                  <article class="mf-letter__mode-card">
                    <div class="mf-letter__mode-row">
                      <span class="mf-letter__chip mf-letter__chip--yellow">에너지 스타일</span>
                      <span class="mf-letter__mode-value">
                        {{ letterFree?.에너지스타일 || '-' }}
                      </span>
                    </div>
                    <div class="mf-letter__mode-row">
                      <span class="mf-letter__chip mf-letter__chip--green">이달의 캐릭터</span>
                      <div class="mf-letter__mode-text">
                        <p class="mf-letter__mode-title">
                          {{ letterFree?.캐릭터 || '' }}
                        </p>
                      </div>
                    </div>
                  </article>
                </section>
              </div>
            </template>

            <!-- 에너지 활성도 전용 레이아웃 -->
            <template v-else-if="isEnergy">
              <header class="mf-letter__header">
                <button
                  type="button"
                  class="mf-letter__close"
                  aria-label="닫기"
                  @click="handleClose"
                >
                  <span class="mf-letter__close-icon"></span>
                </button>
              </header>

              <div class="mf-letter__body">
                <!-- 좌측: 표지 (공용 cover 스타일 재사용) -->
                <section class="mf-letter__cover">
                  <div class="mf-letter__cover-inner">
                  <h3 class="mf-letter__title">오행으로 보는 내 컨디션</h3>
                  <img
                    :src="batteryImg"
                    alt="에너지 활성도 배터리"
                    class="mf-energy__battery"
                  />
                  <div class="mf-letter__cover-text">
                    <p class="mf-letter__lead">이번 달, 내 기운은 어떤 모습일까요?</p>
                    <p class="mf-letter__sub">
                      컨디션은 매달 조금씩 달라져요<br />
                      이번 달의 에너지, 잘 관리해보세요
                    </p>
                  </div>
                  </div>
                </section>

                <div class="mf-letter__divider" aria-hidden="true"></div>

                <!-- 우측: 컨텐츠 -->
                <section class="mf-energy__content">
                  <div class="mf-energy__gauge">
                    <p class="mf-energy__gauge-label">이달의 에너지 활성도</p>
                    <p class="mf-energy__gauge-value">{{ energyPercent }}%</p>
                    <div class="mf-energy__bar">
                      <div
                        class="mf-energy__bar-fill"
                        :style="{ width: `${energyPercent}%` }"
                      ></div>
                    </div>
                    <div class="mf-energy__months">
                      <div class="mf-energy__month">
                        <p>{{ prevMonthLabel || '' }}</p>
                        <p>{{ energyTrend?.전월 != null ? `${energyTrend.전월}%` : '-' }}</p>
                      </div>
                      <div class="mf-energy__month-divider" aria-hidden="true"></div>
                      <div class="mf-energy__month mf-energy__month--current">
                        <p>{{ currMonthLabel || '' }}</p>
                        <p>{{ energyTrend?.이달 != null ? `${energyTrend.이달}%` : `${energyPercent}%` }}</p>
                      </div>
                      <div class="mf-energy__month-divider" aria-hidden="true"></div>
                      <div class="mf-energy__month">
                        <p>{{ nextMonthLabel || '' }}</p>
                        <p>{{ energyTrend?.다음달 != null ? `${energyTrend.다음달}%` : '-' }}</p>
                      </div>
                    </div>
                  </div>

                  <article class="mf-energy__summary">
                    <p v-html="nl2br(energyFree?.설명)"></p>
                    <p>
                      {{ energyPercent }}%는
                      <span class="mf-energy__summary-accent">{{ energyFree?.방향 || '' }}</span>에 해당해요.
                    </p>
                  </article>

                  <div class="mf-energy__categories">
                    <div class="mf-energy__category mf-energy__category--love">
                      <p class="mf-energy__category-title">💘 연애</p>
                      <p class="mf-energy__category-line">
                        {{ energyGuide.love?.제목 || '' }}
                      </p>
                      <p class="mf-energy__category-sub">
                        {{ energyGuide.love?.설명 || '' }}
                      </p>
                    </div>
                    <div class="mf-energy__category mf-energy__category--work">
                      <p class="mf-energy__category-title">📋 업무</p>
                      <p class="mf-energy__category-line">
                        {{ energyGuide.work?.제목 || '' }}
                      </p>
                      <p class="mf-energy__category-sub">
                        {{ energyGuide.work?.설명 || '' }}
                      </p>
                    </div>
                    <div class="mf-energy__category mf-energy__category--money">
                      <p class="mf-energy__category-title">💰 재물</p>
                      <p class="mf-energy__category-line">
                        {{ energyGuide.money?.제목 || '' }}
                      </p>
                      <p class="mf-energy__category-sub">
                        {{ energyGuide.money?.설명 || '' }}
                      </p>
                    </div>
                    <div class="mf-energy__category mf-energy__category--health">
                      <p class="mf-energy__category-title">💪 건강</p>
                      <p class="mf-energy__category-line">
                        {{ energyGuide.health?.제목 || '' }}
                      </p>
                      <p class="mf-energy__category-sub">
                        {{ energyGuide.health?.설명 || '' }}
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            </template>

            <!-- 행운 카드 전용 레이아웃 -->
            <template v-else-if="isLucky">
              <header class="mf-letter__header">
                <button
                  type="button"
                  class="mf-letter__close"
                  aria-label="닫기"
                  @click="handleClose"
                >
                  <span class="mf-letter__close-icon"></span>
                </button>
              </header>

              <div class="mf-letter__body">
                <!-- 좌측: 표지 -->
                <section class="mf-letter__cover">
                  <div class="mf-letter__cover-inner">
                  <h3 class="mf-letter__title">행운카드</h3>
                  <img
                    :src="luckyCardsImg"
                    alt="행운카드"
                    class="mf-lucky__cards"
                  />
                  <div class="mf-letter__cover-text">
                    <p class="mf-letter__lead">이번 달, 당신에게 어떤 행운이 머물고 있을까요?</p>
                    <p class="mf-letter__sub">
                      이번 달의 컬러부터 식탁까지, 일상 속 작은 행운들<br />
                      가까이 두고 한 달을 채워가 보세요
                    </p>
                  </div>
                  </div>
                </section>

                <div class="mf-letter__divider" aria-hidden="true"></div>

                <!-- 우측: 컨텐츠 -->
                <section class="mf-lucky__content">
                  <div v-if="luckyFree?.보충오행" class="mf-lucky__quote">
                    <p>
                      이 달은 <strong>{{ luckyFree.보충오행 }}의 기운</strong>을 가까이 하면 균형이 잡혀요
                    </p>
                  </div>

                  <img :src="luckyCloverImg" alt="" class="mf-lucky__clover" />

                  <div class="mf-lucky__categories">
                    <div class="mf-lucky__category mf-lucky__category--color">
                      <div class="mf-lucky__category-head">
                        <p class="mf-lucky__category-emoji">🎨</p>
                        <p class="mf-lucky__category-label">균형 컬러</p>
                      </div>
                      <div class="mf-lucky__category-value">
                        <p v-for="(c, i) in luckyColorLines" :key="i">{{ c }}</p>
                      </div>
                    </div>
                    <div class="mf-lucky__category mf-lucky__category--food">
                      <div class="mf-lucky__category-head">
                        <p class="mf-lucky__category-emoji">🍳</p>
                        <p class="mf-lucky__category-label">이달의 식탁</p>
                      </div>
                      <div class="mf-lucky__category-value">
                        <p v-for="(f, i) in luckyFoodLines" :key="i">{{ f }}</p>
                      </div>
                    </div>
                    <div class="mf-lucky__category mf-lucky__category--date">
                      <div class="mf-lucky__category-head">
                        <p class="mf-lucky__category-emoji">📆</p>
                        <p class="mf-lucky__category-label">행운 날짜</p>
                      </div>
                      <div class="mf-lucky__category-value">
                        <p v-for="(d, i) in luckyDayLines" :key="i">{{ d }}</p>
                      </div>
                    </div>
                    <div class="mf-lucky__category mf-lucky__category--number">
                      <div class="mf-lucky__category-head">
                        <p class="mf-lucky__category-emoji">🔢</p>
                        <p class="mf-lucky__category-label">균형 숫자</p>
                      </div>
                      <div class="mf-lucky__category-value">
                        <p>{{ luckyNumbers || '' }}</p>
                      </div>
                    </div>
                    <div class="mf-lucky__category mf-lucky__category--energy">
                      <div class="mf-lucky__category-head">
                        <p class="mf-lucky__category-emoji">⚡</p>
                        <p class="mf-lucky__category-label">에너지 충전</p>
                      </div>
                      <div class="mf-lucky__category-value">
                        <p v-for="(e, i) in luckyEnergyLines" :key="i">{{ e }}</p>
                      </div>
                    </div>
                    <div class="mf-lucky__category mf-lucky__category--romance">
                      <div class="mf-lucky__category-head">
                        <p class="mf-lucky__category-emoji">⭐</p>
                        <p class="mf-lucky__category-label">나를 위한 낭만</p>
                      </div>
                      <div v-if="luckyFree?.낭만한마디" class="mf-lucky__category-value">
                        <p>{{ `"${luckyFree.낭만한마디}"` }}</p>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </template>

            <!-- 내 연애 감정 전용 레이아웃 -->
            <template v-else-if="isRomance">
              <header class="mf-letter__header">
                <button
                  type="button"
                  class="mf-letter__close"
                  aria-label="닫기"
                  @click="handleClose"
                >
                  <span class="mf-letter__close-icon"></span>
                </button>
              </header>

              <div class="mf-letter__body">
                <!-- 좌측: 표지 -->
                <section class="mf-letter__cover">
                  <div class="mf-letter__cover-inner">
                  <h3 class="mf-letter__title">내 연애 감정</h3>
                  <img
                    :src="romanceHeartImg"
                    alt="내 연애 감정"
                    class="mf-romance__heart"
                  />
                  <div class="mf-letter__cover-text">
                    <p class="mf-letter__lead">이번 달, 당신의 마음은 어디를 향하고 있을까요?</p>
                    <p class="mf-letter__sub">
                      말로 다 전하지 못한 마음들이 있는지<br />
                      당신 안에 머물고 있는 진짜 마음을 확인해보세요
                    </p>
                  </div>
                  </div>
                </section>

                <div class="mf-letter__divider" aria-hidden="true"></div>

                <!-- 우측: 컨텐츠 -->
                <section class="mf-romance__content">
                  <div class="mf-romance__headline">
                    <p v-if="romanceFree?.유형" class="mf-romance__headline-title">
                      {{ `“${romanceFree.유형}”` }}
                    </p>
                    <p class="mf-romance__headline-desc" v-html="nl2br(romanceFree?.방향)"></p>
                  </div>

                  <img :src="romanceBowImg" alt="" class="mf-romance__bow" />

                  <div class="mf-romance__card">
                    <div class="mf-romance__row">
                      <span class="mf-romance__chip">전반</span>
                      <p v-html="nl2br(romanceFree?.시기별?.전반)"></p>
                    </div>
                    <div class="mf-romance__divider" aria-hidden="true"></div>
                    <div class="mf-romance__row">
                      <span class="mf-romance__chip">후반</span>
                      <p v-html="nl2br(romanceFree?.시기별?.후반)"></p>
                    </div>
                  </div>

                  <div class="mf-romance__card">
                    <div class="mf-romance__row">
                      <p class="mf-romance__row-title">💑 파트너가 있다면</p>
                      <p class="mf-romance__row-sub" v-html="nl2br(romanceFree?.파트너)"></p>
                    </div>
                    <div class="mf-romance__divider" aria-hidden="true"></div>
                    <div class="mf-romance__row">
                      <p class="mf-romance__row-title">🙋 새로운 만남을 기다리고 있다면</p>
                      <p class="mf-romance__row-sub" v-html="nl2br(romanceFree?.새만남)"></p>
                    </div>
                  </div>
                </section>
              </div>
            </template>

            <!-- 내 건강 전용 레이아웃 -->
            <template v-else-if="isHealth">
              <header class="mf-letter__header">
                <button
                  type="button"
                  class="mf-letter__close"
                  aria-label="닫기"
                  @click="handleClose"
                >
                  <span class="mf-letter__close-icon"></span>
                </button>
              </header>

              <div class="mf-letter__body">
                <!-- 좌측: 표지 -->
                <section class="mf-letter__cover">
                  <div class="mf-letter__cover-inner">
                  <h3 class="mf-letter__title">내 건강은 내가 챙겨야지</h3>
                  <img
                    :src="healthClipboardImg"
                    alt="내 건강은 내가 챙겨야지"
                    class="mf-health__clipboard"
                  />
                  <div class="mf-letter__cover-text">
                    <p class="mf-letter__lead">이번 달, 내 몸과 마음의 균형은 잘 맞춰지고 있을까요</p>
                    <p class="mf-letter__sub">
                      몸과 마음이 보내는 작은 변화에 귀 기울이며<br />
                      나만을 위한 휴식과 에너지를 채워가 보세요
                    </p>
                  </div>
                  </div>
                </section>

                <div class="mf-letter__divider" aria-hidden="true"></div>

                <!-- 우측: 컨텐츠 -->
                <section class="mf-health__content">
                  <div class="mf-health__summary">
                    <p v-html="nl2br(healthFree?.케어방향)"></p>
                    <p v-if="healthFree?.증상">
                      <span class="mf-health__accent">{{ healthFree?.주의부위 || '' }}</span> — <span v-html="nl2br(healthFree.증상)"></span>
                    </p>
                  </div>

                  <div class="mf-health__card">
                    <div class="mf-health__period">
                      <p class="mf-health__period-title">
                        월 초({{ healthFree?.시기별?.월초?.기간 || '' }})
                      </p>
                      <div class="mf-health__period-desc">
                        <p v-html="nl2br(healthFree?.시기별?.월초?.상태)"></p>
                        <p v-html="nl2br(healthFree?.시기별?.월초?.가이드)"></p>
                      </div>
                      <div class="mf-health__period-action">
                        <span class="mf-health__chip mf-health__chip--green">좋은 활동</span>
                        <p v-html="nl2br(healthFree?.시기별?.월초?.활동)"></p>
                      </div>
                    </div>
                    <div class="mf-health__divider" aria-hidden="true"></div>
                    <div class="mf-health__period">
                      <p class="mf-health__period-title">
                        월 중순({{ healthFree?.시기별?.중순?.기간 || '' }})
                      </p>
                      <div class="mf-health__period-desc">
                        <p v-html="nl2br(healthFree?.시기별?.중순?.상태)"></p>
                        <p v-html="nl2br(healthFree?.시기별?.중순?.가이드)"></p>
                      </div>
                      <div class="mf-health__period-action">
                        <span class="mf-health__chip mf-health__chip--green">좋은 활동</span>
                        <p v-html="nl2br(healthFree?.시기별?.중순?.활동)"></p>
                      </div>
                    </div>
                    <div class="mf-health__divider" aria-hidden="true"></div>
                    <div class="mf-health__period">
                      <p class="mf-health__period-title">
                        월 후반({{ healthFree?.시기별?.후반?.기간 || '' }})
                      </p>
                      <div class="mf-health__period-desc">
                        <p v-html="nl2br(healthFree?.시기별?.후반?.상태)"></p>
                        <p v-html="nl2br(healthFree?.시기별?.후반?.가이드)"></p>
                      </div>
                      <div class="mf-health__period-action">
                        <span class="mf-health__chip mf-health__chip--green">좋은 활동</span>
                        <p v-html="nl2br(healthFree?.시기별?.후반?.활동)"></p>
                      </div>
                    </div>
                  </div>

                  <div class="mf-health__care">
                    <div class="mf-health__care-row">
                      <span class="mf-health__chip mf-health__chip--yellow">이달의 케어 포인트</span>
                      <p v-html="nl2br(healthFree?.케어포인트)"></p>
                    </div>
                    <div v-if="healthFree?.보충활동?.마인드" class="mf-health__care-row">
                      <span class="mf-health__chip mf-health__chip--purple">마음 한 줄</span>
                      <p>{{ `“${healthFree.보충활동.마인드}”` }}</p>
                    </div>
                  </div>
                </section>
              </div>
            </template>

            <!-- 이달 내 연애 스타일 전용 레이아웃 -->
            <template v-else-if="isRomanceStyle">
              <header class="mf-letter__header">
                <button
                  type="button"
                  class="mf-letter__close"
                  aria-label="닫기"
                  @click="handleClose"
                >
                  <span class="mf-letter__close-icon"></span>
                </button>
              </header>

              <div class="mf-letter__body">
                <!-- 좌측: 표지 -->
                <section class="mf-letter__cover">
                  <div class="mf-letter__cover-inner">
                  <div class="mf-rstyle__title-group">
                    <span class="mf-rstyle__premium-badge">P</span>
                    <h3 class="mf-letter__title">이 달, 내 연애 스타일은?</h3>
                  </div>
                  <img
                    :src="romanceStyleHeartImg"
                    alt="이 달, 내 연애 스타일은?"
                    class="mf-rstyle__heart"
                  />
                  <div class="mf-letter__cover-text">
                    <p class="mf-letter__lead">당신의 사랑, 이번 달은 어떻게 흐를까요</p>
                    <p class="mf-letter__sub">
                      데이트하기 좋은 날과 마음의 여유가 필요한 날<br />
                      이 달의 연애 스타일을 미리 짚어드려요
                    </p>
                  </div>
                  </div>
                </section>

                <div class="mf-letter__divider" aria-hidden="true"></div>

                <!-- 우측: 컨텐츠 (스크롤 가능) -->
                <section class="mf-rstyle__content">
                  <!-- 변화 카드 -->
                  <div class="mf-rstyle__shift">
                    <span class="mf-rstyle__shift-badge">이 달의 연애 스타일 변화</span>
                    <div class="mf-rstyle__shift-inner">
                      <div class="mf-rstyle__shift-types">
                        <p>{{ romanceStyleShift.from.emoji }} {{ romanceStyleShift.from.label }}</p>
                        <p>→</p>
                        <p>{{ romanceStyleShift.to.emoji }} {{ romanceStyleShift.to.label }}</p>
                      </div>
                      <div class="mf-rstyle__shift-desc">
                        <p v-for="(line, i) in romanceStyleShift.desc" :key="i" v-html="line"></p>
                      </div>
                    </div>
                  </div>

                  <!-- 데이트 좋은 날 -->
                  <div class="mf-rstyle__section mf-rstyle__section--good">
                    <svg class="mf-rstyle__tab" width="193" height="57" viewBox="0 0 193.21 57" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0 10C0 4.47715 4.47715 0 10 0H165.672C169.536 0 173.055 2.22667 174.709 5.71914L192.235 42.7191C195.378 49.3536 190.539 57 183.198 57H10C4.47715 57 0 52.5228 0 47V10Z" fill="#FFEABA"/>
                    </svg>
                    <div class="mf-rstyle__section-body">
                      <p class="mf-rstyle__section-title">💘 데이트 하기 좋은 날!</p>
                      <div
                        v-for="(d, i) in romanceStyleGoodDays"
                        :key="`good-${i}`"
                        class="mf-rstyle__day-row"
                      >
                        <span class="mf-rstyle__day-chip mf-rstyle__day-chip--good">{{ d.day }}</span>
                        <div class="mf-rstyle__day-text">
                          <p v-for="(line, j) in d.lines" :key="j" v-html="line"></p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 감정 과해지는 날 -->
                  <div class="mf-rstyle__section mf-rstyle__section--warn">
                    <svg class="mf-rstyle__tab" width="193" height="57" viewBox="0 0 193.21 57" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0 10C0 4.47715 4.47715 0 10 0H165.672C169.536 0 173.055 2.22667 174.709 5.71914L192.235 42.7191C195.378 49.3536 190.539 57 183.198 57H10C4.47715 57 0 52.5228 0 47V10Z" fill="#FFBDBD"/>
                    </svg>
                    <div class="mf-rstyle__section-body">
                      <p class="mf-rstyle__section-title">👀 감정이 과해지기 쉬운 날</p>
                      <div
                        v-for="(d, i) in romanceStyleWarnDays"
                        :key="`warn-${i}`"
                        class="mf-rstyle__day-row"
                      >
                        <span class="mf-rstyle__day-chip mf-rstyle__day-chip--warn">{{ d.day }}</span>
                        <div class="mf-rstyle__day-text">
                          <p v-for="(line, j) in d.lines" :key="j" v-html="line"></p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- TIP 카드 -->
                  <div class="mf-rstyle__tip">
                    <span class="mf-rstyle__tip-chip">💡 TIP</span>
                    <div class="mf-rstyle__tip-text">
                      <p v-for="(line, i) in romanceStyleTipLines" :key="i" v-html="line"></p>
                    </div>
                    <p v-if="romanceStyleTipExtra" class="mf-rstyle__tip-extra">{{ romanceStyleTipExtra }}</p>
                  </div>

                  <!-- 이달 필요한 감성 -->
                  <div class="mf-rstyle__section mf-rstyle__section--mood">
                    <svg class="mf-rstyle__tab" width="193" height="57" viewBox="0 0 193.21 57" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0 10C0 4.47715 4.47715 0 10 0H165.672C169.536 0 173.055 2.22667 174.709 5.71914L192.235 42.7191C195.378 49.3536 190.539 57 183.198 57H10C4.47715 57 0 52.5228 0 47V10Z" fill="#AAC0E9"/>
                    </svg>
                    <div class="mf-rstyle__section-body">
                      <p class="mf-rstyle__section-title">🌙 이 달, 내가 필요한 감성은?</p>
                      <div class="mf-rstyle__mood-head">
                        <span class="mf-rstyle__mood-chip">{{ romanceStyleMoodBadge }}</span>
                        <div class="mf-rstyle__mood-desc">
                          <p v-for="(line, i) in romanceStyleMoodDesc" :key="i" v-html="line"></p>
                        </div>
                      </div>
                      <div
                        v-for="(card, i) in romanceStyleMoodAdvices"
                        :key="`mood-${i}`"
                        class="mf-rstyle__mood-card"
                      >
                        <p class="mf-rstyle__mood-card-title">{{ card.title }}</p>
                        <div class="mf-rstyle__mood-card-text">
                          <p v-for="(line, j) in card.texts" :key="j" v-html="line"></p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </template>

            <!-- 소비위험일 · 알뜰쇼핑 · 재물팁 전용 레이아웃 (rstyle 패턴 재사용) -->
            <template v-else-if="isMoney">
              <header class="mf-letter__header">
                <button
                  type="button"
                  class="mf-letter__close"
                  aria-label="닫기"
                  @click="handleClose"
                >
                  <span class="mf-letter__close-icon"></span>
                </button>
              </header>

              <div class="mf-letter__body">
                <!-- 좌측: 표지 -->
                <section class="mf-letter__cover">
                  <div class="mf-letter__cover-inner">
                  <div class="mf-rstyle__title-group">
                    <span class="mf-rstyle__premium-badge">P</span>
                    <h3 class="mf-letter__title">소비위험일 · 알뜰쇼핑 · 재물팁</h3>
                  </div>
                  <img
                    :src="moneyCoinsImg"
                    alt="소비위험일 · 알뜰쇼핑 · 재물팁"
                    class="mf-rstyle__heart"
                  />
                  <div class="mf-letter__cover-text">
                    <p class="mf-letter__lead">재물은 잘 쓰고 잘 관리해야 든든하게 모여요</p>
                    <p class="mf-letter__sub">
                      지출과 기회가 오가는 이 달의 재물 흐름<br />
                      조심할 날부터 챙길 정보까지 알고 가면 든든해요
                    </p>
                  </div>
                  </div>
                </section>

                <div class="mf-letter__divider" aria-hidden="true"></div>

                <!-- 우측: 컨텐츠 (스크롤 가능) -->
                <section class="mf-rstyle__content">
                  <!-- 재물 방향 카드 -->
                  <div class="mf-rstyle__shift">
                    <span class="mf-rstyle__shift-badge">이달의 재물 방향</span>
                    <div class="mf-rstyle__shift-inner">
                      <div class="mf-rstyle__shift-types">
                        <p>{{ moneyShift.emoji }} {{ moneyShift.label }}</p>
                      </div>
                      <div class="mf-rstyle__shift-desc">
                        <p class="mf-money__shift-headline" v-html="moneyShift.headline"></p>
                        <p v-for="(line, i) in moneyShift.desc" :key="i" v-html="line"></p>
                      </div>
                    </div>
                  </div>

                  <!-- 소비 위험일 -->
                  <div class="mf-rstyle__section mf-rstyle__section--good">
                    <svg class="mf-rstyle__tab" width="193" height="57" viewBox="0 0 193.21 57" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0 10C0 4.47715 4.47715 0 10 0H165.672C169.536 0 173.055 2.22667 174.709 5.71914L192.235 42.7191C195.378 49.3536 190.539 57 183.198 57H10C4.47715 57 0 52.5228 0 47V10Z" fill="#FFEABA"/>
                    </svg>
                    <div class="mf-rstyle__section-body">
                      <p class="mf-rstyle__section-title">💸 소비 위험일</p>
                      <div
                        v-for="(d, i) in moneyDangerDays"
                        :key="`danger-${i}`"
                        class="mf-money__day-row"
                      >
                        <span class="mf-rstyle__day-chip mf-rstyle__day-chip--good">{{ d.day }}</span>
                        <div class="mf-rstyle__day-text">
                          <p v-for="(line, j) in d.lines" :key="j" v-html="line"></p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 알뜰 쇼핑일 -->
                  <div class="mf-rstyle__section mf-rstyle__section--warn">
                    <svg class="mf-rstyle__tab" width="193" height="57" viewBox="0 0 193.21 57" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0 10C0 4.47715 4.47715 0 10 0H165.672C169.536 0 173.055 2.22667 174.709 5.71914L192.235 42.7191C195.378 49.3536 190.539 57 183.198 57H10C4.47715 57 0 52.5228 0 47V10Z" fill="#FFBDBD"/>
                    </svg>
                    <div class="mf-rstyle__section-body">
                      <p class="mf-rstyle__section-title">🛒 알뜰 쇼핑일</p>
                      <div
                        v-for="(d, i) in moneyThriftyDays"
                        :key="`thrifty-${i}`"
                        class="mf-money__day-row"
                      >
                        <span class="mf-rstyle__day-chip mf-rstyle__day-chip--warn">{{ d.day }}</span>
                        <div class="mf-rstyle__day-text">
                          <p v-for="(line, j) in d.lines" :key="j" v-html="line"></p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- TIP -->
                  <div class="mf-rstyle__tip">
                    <span class="mf-rstyle__tip-chip">💡 TIP</span>
                    <div class="mf-rstyle__tip-text">
                      <p v-for="(line, i) in moneyTipLines" :key="i" v-html="line"></p>
                    </div>
                    <p v-if="moneyTipExtra" class="mf-rstyle__tip-extra">{{ moneyTipExtra }}</p>
                  </div>

                  <!-- 시기별 가이드 -->
                  <div class="mf-rstyle__section mf-rstyle__section--mood">
                    <svg class="mf-rstyle__tab" width="193" height="57" viewBox="0 0 193.21 57" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0 10C0 4.47715 4.47715 0 10 0H165.672C169.536 0 173.055 2.22667 174.709 5.71914L192.235 42.7191C195.378 49.3536 190.539 57 183.198 57H10C4.47715 57 0 52.5228 0 47V10Z" fill="#AAC0E9"/>
                    </svg>
                    <div class="mf-rstyle__section-body">
                      <p class="mf-rstyle__section-title">📅 시기별 가이드</p>
                      <div
                        v-for="(p, i) in moneyPeriodGuides"
                        :key="`period-${i}`"
                        class="mf-money__day-row"
                      >
                        <span class="mf-money__period-chip">{{ p.key }}</span>
                        <div class="mf-rstyle__day-text">
                          <p v-for="(line, j) in p.lines" :key="j" v-html="line"></p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 이달의 재물 TIP -->
                  <div class="mf-rstyle__tip">
                    <span class="mf-rstyle__tip-chip">💡 이달의 재물 TIP</span>
                    <ul class="mf-money__bullet-list">
                      <li v-for="(tip, i) in moneyMonthlyTips" :key="i">{{ tip }}</li>
                    </ul>
                  </div>
                </section>
              </div>
            </template>

            <!-- 이달 나의 역량 전용 레이아웃 -->
            <template v-else-if="isCapability">
              <header class="mf-letter__header">
                <button
                  type="button"
                  class="mf-letter__close"
                  aria-label="닫기"
                  @click="handleClose"
                >
                  <span class="mf-letter__close-icon"></span>
                </button>
              </header>

              <div class="mf-letter__body">
                <!-- 좌측: 표지 -->
                <section class="mf-letter__cover">
                  <div class="mf-letter__cover-inner">
                  <div class="mf-rstyle__title-group">
                    <span class="mf-rstyle__premium-badge">P</span>
                    <h3 class="mf-letter__title">이 달, 어떤 내 모습이 빛날 수 있을까?</h3>
                  </div>
                  <img
                    :src="capabilityTrophyImg"
                    alt="이 달, 어떤 내 모습이 빛날 수 있을까?"
                    class="mf-rstyle__heart"
                  />
                  <div class="mf-letter__cover-text">
                    <p class="mf-letter__lead">이 달, 내 안의 어떤 힘이 가장 빛나게 될까요</p>
                    <p class="mf-letter__sub">
                      누구에게나 숨겨진 다양한 능력이 있어요<br />
                      이 달 가장 빛날 내 강점을 미리 만나보세요
                    </p>
                  </div>
                  </div>
                </section>

                <div class="mf-letter__divider" aria-hidden="true"></div>

                <!-- 우측: 컨텐츠 (스크롤) -->
                <section class="mf-rstyle__content">
                  <!-- 통계 카드 -->
                  <div class="mf-cap__stats">
                    <div v-for="stat in capabilityStats" :key="stat.label" class="mf-cap__stat-row">
                      <p class="mf-cap__stat-label">{{ stat.label }}</p>
                      <div class="mf-cap__stat-bar">
                        <div
                          class="mf-cap__stat-fill"
                          :style="{ width: `${stat.value}%`, background: stat.color }"
                        ></div>
                      </div>
                      <p class="mf-cap__stat-value">{{ stat.value }}%</p>
                    </div>
                  </div>

                  <!-- 업무에서 -->
                  <div class="mf-rstyle__section mf-rstyle__section--good">
                    <svg class="mf-rstyle__tab" width="193" height="57" viewBox="0 0 193.21 57" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0 10C0 4.47715 4.47715 0 10 0H165.672C169.536 0 173.055 2.22667 174.709 5.71914L192.235 42.7191C195.378 49.3536 190.539 57 183.198 57H10C4.47715 57 0 52.5228 0 47V10Z" fill="#FFEABA"/>
                    </svg>
                    <div class="mf-rstyle__section-body">
                      <p class="mf-rstyle__section-title">📊 업무에서</p>
                      <div
                        v-for="card in capabilityWorkCards"
                        :key="card.label"
                        class="mf-money__day-row"
                      >
                        <span class="mf-cap__chip" :style="{ background: card.color }">{{ card.label }} - {{ card.value }}%</span>
                        <div class="mf-rstyle__day-text">
                          <p v-for="(line, i) in card.lines" :key="i" v-html="line"></p>
                        </div>
                      </div>
                      <div class="mf-cap__keywords">
                        <span v-for="kw in capabilityKeywords" :key="kw" class="mf-cap__keyword">{{ kw }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- 관계에서 -->
                  <div class="mf-rstyle__section mf-rstyle__section--warn">
                    <svg class="mf-rstyle__tab" width="193" height="57" viewBox="0 0 193.21 57" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0 10C0 4.47715 4.47715 0 10 0H165.672C169.536 0 173.055 2.22667 174.709 5.71914L192.235 42.7191C195.378 49.3536 190.539 57 183.198 57H10C4.47715 57 0 52.5228 0 47V10Z" fill="#FFBDBD"/>
                    </svg>
                    <div class="mf-rstyle__section-body">
                      <p class="mf-rstyle__section-title">🤝 관계에서</p>
                      <div class="mf-cap__rel-intro">
                        <p v-for="(line, i) in capabilityRelation.introLines" :key="i" v-html="line"></p>
                      </div>
                      <div class="mf-money__day-row">
                        <span class="mf-cap__rel-type">{{ capabilityRelation.style }}</span>
                        <div class="mf-rstyle__day-text">
                          <p v-for="(line, i) in capabilityRelation.tipLines" :key="i" v-html="line"></p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 종합 -->
                  <div class="mf-rstyle__tip mf-rstyle__tip--center">
                    <span class="mf-rstyle__tip-chip">종합</span>
                    <p class="mf-rstyle__tip-extra" v-html="capabilitySummary"></p>
                  </div>
                </section>
              </div>
            </template>

            <!-- 이달 조심할 것 전용 레이아웃 -->
            <template v-else-if="isCaution">
              <header class="mf-letter__header">
                <button
                  type="button"
                  class="mf-letter__close"
                  aria-label="닫기"
                  @click="handleClose"
                >
                  <span class="mf-letter__close-icon"></span>
                </button>
              </header>

              <div class="mf-letter__body">
                <!-- 좌측: 표지 -->
                <section class="mf-letter__cover">
                  <div class="mf-letter__cover-inner">
                  <div class="mf-rstyle__title-group">
                    <span class="mf-rstyle__premium-badge">P</span>
                    <h3 class="mf-letter__title">뭔가 조심해야 할 게 있을까?</h3>
                  </div>
                  <img
                    :src="cautionWarningImg"
                    alt="이달, 뭔가 조심해야 할 게 있을까?"
                    class="mf-rstyle__heart"
                  />
                  <div class="mf-letter__cover-text">
                    <p class="mf-letter__lead">무엇보다 마음 편한 한 달이 최고죠</p>
                    <p class="mf-letter__sub">
                      스쳐 지나가기 쉬운 작은 순간들을 가볍게 돌아보며<br />
                      이 달에 필요한 주의와 힌트를 미리 짚어드려요
                    </p>
                  </div>
                  </div>
                </section>

                <div class="mf-letter__divider" aria-hidden="true"></div>

                <!-- 우측: 컨텐츠 (스크롤) -->
                <section class="mf-rstyle__content">
                  <!-- 등급 + 상황 -->
                  <div class="mf-rstyle__section mf-rstyle__section--good">
                    <svg class="mf-rstyle__tab" width="193" height="57" viewBox="0 0 193.21 57" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0 10C0 4.47715 4.47715 0 10 0H165.672C169.536 0 173.055 2.22667 174.709 5.71914L192.235 42.7191C195.378 49.3536 190.539 57 183.198 57H10C4.47715 57 0 52.5228 0 47V10Z" fill="#FFEABA"/>
                    </svg>
                    <div class="mf-rstyle__section-body">
                      <p class="mf-rstyle__section-title">{{ cautionGradeEmoji(cautionGrade) }} {{ cautionGrade }}</p>
                      <p class="mf-caution__lead" v-html="cautionLead"></p>
                      <div
                        v-for="(s, i) in cautionSituations"
                        :key="`sit-${i}`"
                        class="mf-money__day-row"
                      >
                        <span :class="`mf-caution__chip mf-caution__chip--${cautionChipColor(s.유형)}`">{{ s.제목 }}</span>
                        <div v-if="s.상황lines.length" class="mf-rstyle__day-text">
                          <p v-for="(line, j) in s.상황lines" :key="`s-${j}`" v-html="line"></p>
                        </div>
                        <div v-if="s.대처lines.length" class="mf-rstyle__day-text">
                          <p v-for="(line, j) in s.대처lines" :key="`d-${j}`" v-html="line"></p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 생활체크 TIP -->
                  <div class="mf-rstyle__tip">
                    <span class="mf-rstyle__tip-chip">💡 생활체크</span>
                    <div class="mf-rstyle__tip-text">
                      <p v-for="(line, i) in cautionLifestyle" :key="i" v-html="line"></p>
                    </div>
                  </div>

                  <!-- 체크 날짜 -->
                  <div class="mf-rstyle__section mf-rstyle__section--warn">
                    <svg class="mf-rstyle__tab" width="193" height="57" viewBox="0 0 193.21 57" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0 10C0 4.47715 4.47715 0 10 0H165.672C169.536 0 173.055 2.22667 174.709 5.71914L192.235 42.7191C195.378 49.3536 190.539 57 183.198 57H10C4.47715 57 0 52.5228 0 47V10Z" fill="#FFBDBD"/>
                    </svg>
                    <div class="mf-rstyle__section-body">
                      <p class="mf-rstyle__section-title">🛑 체크 날짜</p>
                      <div
                        v-for="(d, i) in cautionCheckDays"
                        :key="`chk-${i}`"
                        class="mf-money__day-row"
                      >
                        <span class="mf-rstyle__day-chip mf-rstyle__day-chip--warn">{{ d.day }}</span>
                        <div class="mf-rstyle__day-text">
                          <p v-for="(line, j) in d.lines" :key="j" v-html="line"></p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </template>

            <!-- 기운 따라 달라지는 나 전용 레이아웃 -->
            <template v-else-if="isMode">
              <header class="mf-letter__header">
                <button
                  type="button"
                  class="mf-letter__close"
                  aria-label="닫기"
                  @click="handleClose"
                >
                  <span class="mf-letter__close-icon"></span>
                </button>
              </header>

              <div class="mf-letter__body">
                <!-- 좌측: 표지 -->
                <section class="mf-letter__cover">
                  <div class="mf-letter__cover-inner">
                  <div class="mf-rstyle__title-group">
                    <span class="mf-rstyle__premium-badge">P</span>
                    <h3 class="mf-letter__title">기운 따라 달라지는 나</h3>
                  </div>
                  <img
                    :src="modeMirrorImg"
                    alt="기운 따라 달라지는 나"
                    class="mf-rstyle__heart"
                  />
                  <div class="mf-letter__cover-text">
                    <p class="mf-letter__lead">이번 달, 어떤 나를 만나게 될까요</p>
                    <p class="mf-letter__sub">
                      매달 조금씩 달라지는 내 모습<br />
                      변화하는 내 감성을 살펴볼 수 있어요
                    </p>
                  </div>
                  </div>
                </section>

                <div class="mf-letter__divider" aria-hidden="true"></div>

                <!-- 우측: 컨텐츠 (스크롤) -->
                <section class="mf-rstyle__content">
                  <!-- 이달의 나는? -->
                  <div class="mf-rstyle__shift">
                    <span class="mf-rstyle__shift-badge">{{ currMonthLabel }}의 나는?</span>
                    <div class="mf-rstyle__shift-inner mf-mode__intro">
                      <div class="mf-mode__intro-head">
                        <p class="mf-mode__mode-title">{{ modeTitle }}</p>
                        <span class="mf-mode__mode-mbti">{{ modeMbtiTransition }}</span>
                      </div>
                      <div class="mf-mode__intro-body">
                        <p class="mf-mode__level">
                          <span v-if="modeLevelBar" class="mf-mode__level-dots">{{ modeLevelBar }}</span>
                          <span v-if="modeLevelDesc" class="mf-mode__level-text">{{ modeLevelDesc }}</span>
                        </p>
                        <p class="mf-mode__desc" v-html="modeDescText"></p>
                      </div>
                    </div>
                  </div>

                  <!-- TIP -->
                  <div class="mf-rstyle__tip">
                    <span class="mf-rstyle__tip-chip">💡 TIP</span>
                    <div class="mf-rstyle__tip-text">
                      <p v-for="(line, i) in modeIntroLines" :key="i" v-html="line"></p>
                    </div>
                  </div>

                  <!-- MBTI 축별 변화 -->
                  <div class="mf-rstyle__section mf-rstyle__section--good">
                    <svg class="mf-rstyle__tab" width="193" height="57" viewBox="0 0 193.21 57" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0 10C0 4.47715 4.47715 0 10 0H165.672C169.536 0 173.055 2.22667 174.709 5.71914L192.235 42.7191C195.378 49.3536 190.539 57 183.198 57H10C4.47715 57 0 52.5228 0 47V10Z" fill="#FFEABA"/>
                    </svg>
                    <div class="mf-rstyle__section-body mf-mode__axes">
                      <div
                        v-for="(axis, idx) in modeAxes"
                        :key="axis.pair"
                        class="mf-mode__axis"
                      >
                        <span class="mf-mode__axis-badge">{{ axis.pair }}</span>
                        <div class="mf-mode__bars">
                          <div class="mf-mode__bar-row">
                            <span class="mf-mode__bar-label">나는</span>
                            <span class="mf-mode__letter-chip" style="background:#949494">{{ axis.me.letter }}</span>
                            <div class="mf-mode__bar">
                              <div
                                class="mf-mode__bar-fill"
                                :style="{ width: `${axis.me.value}%`, background: '#949494' }"
                              ></div>
                            </div>
                            <span class="mf-mode__bar-value">{{ axis.me.value }}%</span>
                          </div>
                          <img
                            class="mf-mode__arrow"
                            :src="modeArrowDownImg"
                            alt=""
                            aria-hidden="true"
                          />
                          <div class="mf-mode__bar-row">
                            <span class="mf-mode__bar-label">이달</span>
                            <span class="mf-mode__letter-chip" :style="{ background: mbtiColor(axis.month.letter) }">{{ axis.month.letter }}</span>
                            <div class="mf-mode__bar">
                              <div
                                class="mf-mode__bar-fill"
                                :style="{ width: `${axis.month.value}%`, background: mbtiColor(axis.month.letter) }"
                              ></div>
                            </div>
                            <span class="mf-mode__bar-value">{{ axis.month.value }}%</span>
                          </div>
                        </div>
                        <div class="mf-mode__state-card">
                          <p class="mf-mode__state-title">{{ axis.state }}</p>
                          <div class="mf-mode__state-text">
                            <p v-for="(line, i) in axis.desc" :key="i" v-html="nl2br(line)"></p>
                          </div>
                        </div>
                        <div v-if="axis.tip" class="mf-mode__inline-tip">
                          <span class="mf-mode__inline-tip-chip">💡 TIP</span>
                          <p v-html="nl2br(axis.tip)"></p>
                        </div>
                        <div v-if="idx < modeAxes.length - 1" class="mf-mode__axis-divider" aria-hidden="true"></div>
                      </div>
                    </div>
                  </div>

                  <!-- 이달 주변 반응 -->
                  <div v-if="modeReactions.length" class="mf-rstyle__section mf-rstyle__section--warn">
                    <svg class="mf-rstyle__tab" width="193" height="57" viewBox="0 0 193.21 57" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0 10C0 4.47715 4.47715 0 10 0H165.672C169.536 0 173.055 2.22667 174.709 5.71914L192.235 42.7191C195.378 49.3536 190.539 57 183.198 57H10C4.47715 57 0 52.5228 0 47V10Z" fill="#FFBDBD"/>
                    </svg>
                    <div class="mf-rstyle__section-body">
                      <p class="mf-rstyle__section-title">🗣 이달 주변 반응</p>
                      <div
                        v-for="(r, i) in modeReactions"
                        :key="`react-${i}`"
                        class="mf-mode__reaction-card"
                      >
                        <div class="mf-mode__reaction-head">
                          <p class="mf-mode__reaction-who">{{ r.who }}</p>
                          <span v-if="r.bubble" class="mf-mode__reaction-bubble" v-html="nl2br(r.bubble)"></span>
                        </div>
                        <p class="mf-mode__reaction-desc" v-html="nl2br(r.desc)"></p>
                      </div>
                    </div>
                  </div>

                  <!-- 이달의 생활 리듬 -->
                  <div v-if="modeLifestyle.length" class="mf-rstyle__section mf-rstyle__section--mood">
                    <svg class="mf-rstyle__tab" width="193" height="57" viewBox="0 0 193.21 57" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0 10C0 4.47715 4.47715 0 10 0H165.672C169.536 0 173.055 2.22667 174.709 5.71914L192.235 42.7191C195.378 49.3536 190.539 57 183.198 57H10C4.47715 57 0 52.5228 0 47V10Z" fill="#AAC0E9"/>
                    </svg>
                    <div class="mf-rstyle__section-body">
                      <p class="mf-rstyle__section-title">🌤 이달의 생활 리듬</p>
                      <div
                        v-for="(t, i) in modeLifestyle"
                        :key="`time-${i}`"
                        class="mf-mode__time-card"
                      >
                        <p class="mf-mode__time-head">{{ t.head }}</p>
                        <div class="mf-mode__time-text">
                          <p v-for="(line, j) in t.lines" :key="j" v-html="line"></p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 다음 달 예고 -->
                  <div v-if="modeNextMonthLines.length" class="mf-mode__next">
                    <p class="mf-mode__next-title">📅 다음 달 예고</p>
                    <div class="mf-mode__next-text">
                      <p v-for="(line, i) in modeNextMonthLines" :key="i" v-html="line"></p>
                    </div>
                  </div>
                </section>
              </div>
            </template>

            <!-- 그 외 카드: 기존 placeholder 유지 -->
            <template v-else>
              <header class="mf-drawer__header">
                <h3 class="mf-drawer__title">
                  <span v-if="card?.emoji" class="mf-drawer__emoji">{{ card.emoji }}</span>
                  {{ card?.title || '' }}
                </h3>
                <button
                  type="button"
                  class="mf-drawer__close"
                  aria-label="닫기"
                  @click="handleClose"
                >
                  <span class="mf-drawer__close-icon"></span>
                </button>
              </header>
              <div class="mf-drawer__body">
                <p class="mf-drawer__sub">{{ card?.description }}</p>
                <div class="mf-drawer__placeholder">
                  <p>이 영역에는 <strong>{{ card?.title }}</strong> 컨텐츠가 들어갑니다.</p>
                  <p class="mf-drawer__placeholder-meta">drawer key: <code>{{ card?.drawer }}</code></p>
                  <div class="mf-drawer__placeholder-block"></div>
                  <div class="mf-drawer__placeholder-block short"></div>
                  <div class="mf-drawer__placeholder-block"></div>
                </div>
              </div>
            </template>
        </aside>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.mf-drawer-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
}

.mf-drawer {
  width: 1084px;
  max-width: 100vw;
  height: 100%;
  background: #fff;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  scrollbar-gutter: stable;
}

.mf-drawer--detail {
  width: 1084px;
  background: #f2f8ef;
  box-shadow: -4px 0 10px rgba(0, 0, 0, 0.1);
}

/* ===== 낭만 편지 전용 ===== */
.mf-letter__header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 18px 40px;
  background: #f2f8ef;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 2;
}
.mf-letter__close {
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.mf-letter__close-icon {
  position: relative;
  width: 16px;
  height: 16px;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 1.5px;
    background: #333;
  }
  &::before {
    transform: translateY(-50%) rotate(45deg);
  }
  &::after {
    transform: translateY(-50%) rotate(-45deg);
  }
}

.mf-letter__body {
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 40px;
  padding: 0 34px 20px 40px;
  flex: 1;
  position: relative;
}

.mf-letter__body > [class$="__content"] {
  align-self: stretch;
  justify-content: flex-start;
  padding-top: 20px;
}
.mf-letter__body > [class$="__content"]:not(.mf-letter__content) > :first-child {
  margin-top: 40px;
}

.mf-letter__cover {
  position: absolute;
  left: 40px;
  top: 0;
  bottom: 0;
  width: 465px;
}

.mf-letter__divider {
  position: absolute;
  left: calc(40px + 465px + 40px);
  top: 0;
  bottom: 0;
  width: 1px;
}

.mf-letter__body > [class$="__content"] {
  margin-left: calc(465px + 40px + 1px + 40px);
  flex: 1;
  min-width: 0;
}

.mf-letter__cover-inner {
  width: 100%;
  height: calc(100vh - 60px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 0;
  position: sticky;
  top: 60px;
}
.mf-letter__title {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 30px;
  font-weight: 700;
  line-height: 1.3;
  color: #333;
  text-align: center;
}
.mf-letter__mailbox {
  width: 280px;
  height: auto;
  margin-top: 5px;
  margin-bottom: 5px;
  margin-left: -40px;
  display: block;
}
.mf-letter__cover-text {
  width: 100%;
  margin-top: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
  color: #333;
}
.mf-letter__lead {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.5;
}
.mf-letter__sub {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
}

.mf-letter__divider {
  width: 1px;
  background: #dcdcdc;
  align-self: stretch;
  flex-shrink: 0;
}

.mf-letter__content {
  flex: 0 0 440px;
  width: 440px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 30px;
  padding: 10px 0;
}
.mf-letter__dove {
  width: 120px;
  height: 120px;
  margin: 0 0 -20px;
  object-fit: contain;
  display: block;
}

.mf-letter__quote {
  width: 100%;
  background: #fff;
  border: 1px solid #74b75a;
  border-radius: 8px;
  padding: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.mf-letter__quote-mark {
  width: 18px;
  height: 13.5px;
  flex-shrink: 0;
  display: block;
}
.mf-letter__quote-mark--open,
.mf-letter__quote-mark--close {
  align-self: flex-start;
}
.mf-letter__quote-mark--close {
  transform: rotate(180deg);
}
.mf-letter__quote-text {
  margin: 0;
  color: #529937;
  font-family: Pretendard, sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  text-align: center;
}

.mf-letter__card {
  width: 100%;
  background: #fff;
  border-radius: 6px;
  padding: 15px;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
}
.mf-letter__card-inner {
  border: 1px solid #74b75a;
  border-radius: 3px;
  padding: 25px 10px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
}
.mf-letter__card-title {
  display: flex;
  justify-content: center;
  width: 100%;
  min-width: 0;

  p {
    display: inline-flex;
    align-items: baseline;
    max-width: 100%;
    min-width: 0;
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 18px;
    font-weight: 700;
    line-height: 30px;
    color: #333;
    padding: 0 24px 6px;
    white-space: nowrap;
    background-image: linear-gradient(to right, #74b75a 50%, transparent 50%);
    background-size: 12px 1px;
    background-repeat: repeat-x;
    background-position: left bottom;
  }
}
.mf-letter__card-title-prefix,
.mf-letter__card-title-suffix {
  flex-shrink: 0;
}
.mf-letter__card-title-user {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mf-letter__accent {
  color: #529936;
}
.mf-letter__lines {
  list-style: none;
  margin: 0;
  padding: 0 10px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 15px;

  li {
    color: #333;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.2;
    padding-bottom: 6px;
    align-self: flex-start;
    background-image: linear-gradient(to right, #74b75a 50%, transparent 50%);
    background-size: 12px 1px;
    background-repeat: repeat-x;
    background-position: left bottom;
  }
}

.mf-letter__mode-card {
  width: 100%;
  background: #fff;
  border: 1px solid #74b75a;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.mf-letter__mode-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.mf-letter__chip {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 12px;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
  color: #fff;
  white-space: nowrap;
}
.mf-letter__chip--yellow {
  background: #f3ae11;
}
.mf-letter__chip--green {
  background: #74b75a;
  min-width: 92px;
}
.mf-letter__mode-value {
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
  color: #333;
  align-self: center;
}
.mf-letter__mode-text {
  display: flex;
  flex-direction: column;
}
.mf-letter__mode-title {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.5;
  color: #333;
}
.mf-letter__mode-desc {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.5;
  color: #333;
}

/* ===== 에너지 활성도 전용 ===== */
.mf-energy__battery {
  width: 220px;
  height: auto;
  margin-top: 30px;
  margin-bottom: 30px;
  display: block;
}

.mf-energy__content {
  flex: 1 1 0;
  max-width: 442px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 30px;
}

.mf-energy__gauge {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}
.mf-energy__gauge-label {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  color: #333;
  text-align: center;
}
.mf-energy__gauge-value {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 30px;
  font-weight: 700;
  line-height: 28px;
  color: #74b75a;
}
.mf-energy__bar {
  position: relative;
  width: 100%;
  height: 10px;
  background: #ebebeb;
  border-radius: 10px;
  overflow: hidden;
}
.mf-energy__bar-fill {
  position: absolute;
  inset: 0 auto 0 0;
  height: 100%;
  border-radius: 10px;
  background: linear-gradient(to right, #a9da96, #74b75a);
}

.mf-energy__months {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}
.mf-energy__month {
  width: 54px;
  height: 54px;
  background: #eee;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  p {
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.3;
    color: #575757;
    text-align: center;
  }
}
.mf-energy__month--current {
  width: 64px;
  height: 64px;
  background: #fff;
  border: 1px solid #48922b;

  p {
    color: #48922b;
    font-weight: 700;
    font-size: 16px;
  }
}
.mf-energy__month-divider {
  width: 16px;
  height: 1px;
  background-image: linear-gradient(to right, #b5b5b5 50%, transparent 50%);
  background-size: 6px 1px;
  background-repeat: repeat-x;
  flex-shrink: 0;
}

.mf-energy__summary {
  background: #fff;
  border-radius: 6px;
  padding: 18px;
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  p {
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.5;
    color: #333;
  }
}
.mf-energy__summary-accent {
  color: #529937;
  font-weight: 700;
}

.mf-energy__categories {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.mf-energy__category {
  background: #fff;
  border: 1px solid #d5d5d5;
  border-radius: 6px;
  padding: 20px 14px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.mf-energy__category-title {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  color: #333;
}
.mf-energy__category-line {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  color: #333;
}
.mf-energy__category-sub {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  color: #575757;
}
.mf-energy__category--love {
  background: #fff5f5;
  border-color: #e3bfbf;
}
.mf-energy__category--work {
  background: #e8f7ff;
  border-color: #a6cde2;
}
.mf-energy__category--money {
  background: #fffbee;
  border-color: #e4d395;
}
.mf-energy__category--health {
  background: #f5fff3;
  border-color: #a4cc9d;
}

/* ===== 행운카드 전용 ===== */
.mf-lucky__cards {
  width: 240px;
  height: auto;
  display: block;
}

.mf-lucky__content {
  flex: 1 1 0;
  max-width: 442px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 30px;
}

.mf-lucky__quote {
  width: 100%;
  background: #fff;
  border-radius: 6px;
  padding: 20px 18px;
  display: flex;
  align-items: center;
  justify-content: center;

  p {
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.5;
    color: #333;
    text-align: center;

    strong {
      font-weight: 700;
    }
  }
}

.mf-lucky__clover {
  width: 140px;
  height: auto;
  display: block;
  margin: -30px 0;
}

.mf-lucky__categories {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  width: 100%;
}
.mf-lucky__category {
  background: #fff;
  border: 1px solid #d5d5d5;
  border-radius: 6px;
  padding: 20px 14px;
  height: 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
}
.mf-lucky__category-head {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}
.mf-lucky__category-emoji {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 25px;
  line-height: 25px;
}
.mf-lucky__category-label {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.5;
  color: #575757;
}
.mf-lucky__category-value {
  display: flex;
  flex-direction: column;
  align-items: center;

  p {
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.5;
    color: #333;
    text-align: center;
  }
}
.mf-lucky__category--color {
  background: #fff5f5;
  border-color: #e3bfbf;
}
.mf-lucky__category--food {
  background: #e8f7ff;
  border-color: #a6cde2;
}
.mf-lucky__category--date {
  background: #fff5ee;
  border-color: #eeceb8;
}
.mf-lucky__category--number {
  background: #f5fff3;
  border-color: #a4cc9d;
}
.mf-lucky__category--energy {
  background: #fffbee;
  border-color: #e4d395;
}
.mf-lucky__category--romance {
  background: #f9f4ff;
  border-color: #bda5d4;
}

/* ===== 내 연애 감정 전용 ===== */
.mf-romance__heart {
  width: 240px;
  height: auto;
  display: block;
}

.mf-romance__content {
  flex: 1 1 0;
  max-width: 442px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 30px;
}

.mf-romance__headline {
  width: 100%;
  background: #fff;
  border: 1px solid #ff6b6b;
  border-radius: 8px;
  padding: 20px 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.mf-romance__headline-title {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: #db4040;
}
.mf-romance__headline-desc {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.5;
  color: #333;
  text-align: center;
}

.mf-romance__bow {
  width: 120px;
  height: auto;
  display: block;
  margin: -12px 0;
  transform: scaleX(-1);
}

.mf-romance__card {
  width: 100%;
  background: #fff;
  border: 1px solid #ff6b6b;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}
.mf-romance__row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
}
.mf-romance__chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 12px;
  background: #ff6b6b;
  color: #fff;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 16px;
}
.mf-romance__row p {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.5;
  color: #333;
}
.mf-romance__row-title {
  font-weight: 700 !important;
  line-height: 20px !important;
}
.mf-romance__row-sub {
  color: #575757 !important;
}
.mf-romance__divider {
  height: 1px;
  background: #ebebeb;
}

/* ===== 내 건강 전용 ===== */
.mf-health__clipboard {
  width: 240px;
  height: auto;
  display: block;
}

.mf-health__content {
  flex: 1 1 0;
  max-width: 442px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 30px;
}

.mf-health__summary {
  background: #fff;
  border-radius: 6px;
  padding: 18px;

  p {
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.5;
    color: #333;
  }
}
.mf-health__accent {
  color: #529937;
  font-weight: 700;
}

.mf-health__card {
  background: #fff;
  border: 1px solid #74b75a;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}
.mf-health__period {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.mf-health__period-title {
  margin: 0;
  padding: 0 5px;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  color: #333;
}
.mf-health__period-desc {
  padding: 8px 5px;

  p {
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.5;
    color: #575757;
  }
}
.mf-health__period-action {
  display: flex;
  align-items: center;
  gap: 8px;

  p {
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.3;
    color: #333;
  }
}
.mf-health__divider {
  height: 1px;
  background: #ebebeb;
}

.mf-health__care {
  background: #fff;
  border: 1px solid #74b75a;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.mf-health__care-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  p {
    margin: 0;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.3;
    color: #333;
  }
}

.mf-health__chip {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 12px;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 16px;
  color: #fff;
  white-space: nowrap;
}
.mf-health__chip--green {
  background: #74b75a;
}
.mf-health__chip--yellow {
  background: #f3ae11;
}
.mf-health__chip--purple {
  background: #9b59b6;
}

/* ===== 이달 내 연애 스타일 전용 ===== */
.mf-rstyle__title-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}
.mf-rstyle__premium-badge {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: #ffbe7a;
  border: 1.5px solid #f09b4e;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Pretendard, sans-serif;
  font-size: 25px;
  font-weight: 800;
  color: #fff7f0;
  line-height: 1;
}
.mf-rstyle__heart {
  width: 280px;
  height: auto;
  display: block;
}

.mf-rstyle__content {
  flex: 1 1 0;
  max-width: 443px;
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 20px 0 40px;
  align-self: stretch;
}

/* 변화 카드 */
.mf-rstyle__shift {
  position: relative;
  padding-top: 13px;
}
.mf-rstyle__shift-badge {
  position: absolute;
  left: 50%;
  top: 0;
  transform: translateX(-50%);
  background: #5e9f1c;
  color: #fff;
  border-radius: 100px;
  padding: 6px 12px;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 700;
  line-height: 16px;
  white-space: nowrap;
  z-index: 1;
}
.mf-rstyle__shift-inner {
  background: #fff;
  border-radius: 8px;
  padding: 30px 40px 25px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.mf-rstyle__shift-types {
  display: flex;
  gap: 12px;
  align-items: center;

  p {
    margin: 0;
    color: #5e9f1c;
    font-family: Pretendard, sans-serif;
    font-size: 20px;
    font-weight: 700;
    line-height: 28px;
  }
}
.mf-rstyle__shift-desc {
  text-align: center;

  p {
    margin: 0;
    color: #333;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.5;
  }
}

/* 색상 섹션 공통 */
.mf-rstyle__section {
  position: relative;
  padding-top: 26px;
}
.mf-rstyle__tab {
  position: absolute;
  top: 0;
  left: 0;
  width: 199px;
  height: 32px;
  display: block;
}

.mf-rstyle__section-body {
  position: relative;
  border-radius: 10px;
  padding: 20px 12px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}
.mf-rstyle__section--good .mf-rstyle__section-body {
  background: #fef7e8;
  filter: drop-shadow(0 -1px 5px rgba(69, 63, 50, 0.1));
}
.mf-rstyle__section--warn .mf-rstyle__section-body {
  background: #fff0f0;
  filter: drop-shadow(0 -1px 5px rgba(94, 60, 60, 0.1));
}
.mf-rstyle__section--mood .mf-rstyle__section-body {
  background: #eef4ff;
}

.mf-rstyle__section-title {
  margin: 0;
  color: #333;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
}

.mf-rstyle__day-row {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.mf-rstyle__day-chip {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 8px;
  border-radius: 12px;
  color: #fff;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 16px;
  white-space: nowrap;
}
.mf-rstyle__day-chip--good { background: #f3ae11; }
.mf-rstyle__day-chip--warn { background: #db4040; }

.mf-rstyle__day-text p {
  margin: 0;
  color: #333;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.5;
}

/* TIP */
.mf-rstyle__tip {
  background: rgba(94, 159, 28, 0.1);
  border: 1px dashed #5e9f1c;
  border-radius: 8px;
  padding: 15px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  text-align: left;
}
.mf-rstyle__tip > .mf-rstyle__tip-chip {
  align-self: center;
}
.mf-rstyle__tip--center {
  align-items: center;
  text-align: center;
}
.mf-rstyle__tip-chip {
  background: #5e9f1c;
  color: #fff;
  border-radius: 12px;
  padding: 3px 8px;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 16px;
}
.mf-rstyle__tip-text p,
.mf-rstyle__tip-extra {
  margin: 0;
  color: #333;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.5;
}

/* 감성 */
.mf-rstyle__mood-head {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 0 8px;
}
.mf-rstyle__mood-chip {
  background: #3f74d4;
  color: #fff;
  border-radius: 5px;
  padding: 4px 10px;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 16px;
}
.mf-rstyle__mood-desc p {
  margin: 0;
  color: #333;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.5;
}
.mf-rstyle__mood-card {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.mf-rstyle__mood-card-title {
  margin: 0;
  color: #333;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
}
.mf-rstyle__mood-card-text p {
  margin: 0;
  color: #575757;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.5;
}

/* ===== 돈 관리 전용 (rstyle 패턴 보강) ===== */
.mf-money__shift-headline {
  font-weight: 700 !important;
}
.mf-money__day-row {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}
.mf-money__period-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 12px;
  background: #3f74d4;
  color: #fff;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 16px;
  white-space: nowrap;
}
.mf-money__bullet-list {
  list-style: disc;
  margin: 0;
  padding: 0 0 0 22px;
  text-align: left;
  width: 100%;

  li {
    color: #333;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.5;
    margin-bottom: 4px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

/* ===== 역량 전용 ===== */
.mf-cap__stats {
  background: #f8f8f8;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 15px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.mf-cap__stat-row {
  display: flex;
  align-items: center;
  gap: 20px;
}
.mf-cap__stat-label {
  margin: 0;
  width: 50px;
  flex-shrink: 0;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
  color: #575757;
}
.mf-cap__stat-bar {
  flex: 1;
  height: 10px;
  background: #ebebeb;
  border-radius: 10px;
  overflow: hidden;
}
.mf-cap__stat-fill {
  height: 100%;
  border-radius: 10px;
}
.mf-cap__stat-value {
  margin: 0;
  width: 36px;
  flex-shrink: 0;
  text-align: right;
  font-family: Pretendard, sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  color: #757575;
}

.mf-cap__chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 8px;
  border-radius: 12px;
  color: #fff;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 16px;
  white-space: nowrap;
}

.mf-cap__keywords {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.mf-cap__keyword {
  background: #fff;
  border-radius: 4px;
  padding: 3px 6px;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
  color: #333;
}

.mf-cap__rel-intro {
  padding: 0 5px;

  p {
    margin: 0;
    color: #333;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.5;
  }
}
.mf-cap__rel-type {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 10px;
  border-radius: 5px;
  background: #db4040;
  color: #fff;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 16px;
  white-space: nowrap;
}

/* ===== 조심 전용 ===== */
.mf-caution__lead {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.4;
  color: #333;
}
.mf-caution__chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 8px;
  border-radius: 12px;
  color: #fff;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 16px;
  white-space: nowrap;
}
.mf-caution__chip--green { background: #5e9f1c; }
.mf-caution__chip--yellow { background: #f3ae11; }
.mf-caution__chip--red { background: #db4040; }

/* ===== 모드 전용 ===== */
.mf-mode__intro {
  gap: 20px !important;
}
.mf-mode__intro-head {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.mf-mode__intro-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 11px;
}
.mf-mode__mode-title {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: #5e9f1c;
  text-align: center;
}
.mf-mode__mode-mbti {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  background: rgba(94, 159, 28, 0.2);
  font-family: Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 500;
  line-height: 1.4;
  color: #333;
  text-align: center;
}
.mf-mode__level {
  margin: 0;
  display: inline-flex;
  align-items: center;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.4;
  color: #333;
  text-align: center;
}
.mf-mode__level-dots {
  display: inline-flex;
  align-items: center;
  letter-spacing: -1px;
  margin-right: 5px;
  line-height: 1;
}
.mf-mode__desc {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.4;
  color: #333;
  text-align: center;
}

.mf-mode__axes {
  gap: 20px !important;
  align-items: stretch !important;
}
.mf-mode__axis {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.mf-mode__axis-badge {
  align-self: flex-start;
  background: #fff;
  border-radius: 100px;
  padding: 3px 8px;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  color: #333;
}
.mf-mode__bars {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}
.mf-mode__bar-row {
  display: flex;
  align-items: center;
  gap: 5px;
  width: 100%;
}
.mf-mode__bar-label {
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
  color: #333;
  flex-shrink: 0;
}
.mf-mode__letter-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 8px;
  border-radius: 12px;
  color: #fff;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
  white-space: nowrap;
  flex-shrink: 0;
}
.mf-mode__bar {
  flex: 1;
  height: 10px;
  background: #ebebeb;
  border-radius: 10px;
  overflow: hidden;
}
.mf-mode__bar-fill {
  height: 100%;
  border-radius: 10px;
}
.mf-mode__bar-value {
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 22px;
  color: #333;
  flex-shrink: 0;
  min-width: 36px;
  text-align: right;
}
.mf-mode__arrow {
  display: block;
  width: 20px;
  height: 20px;
}
.mf-mode__state-card {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.mf-mode__state-title {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.5;
  color: #333;
}
.mf-mode__state-text p {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.5;
  color: #333;
}

.mf-mode__inline-tip {
  background: rgba(243, 174, 17, 0.1);
  border: 1px dashed #f3ae11;
  border-radius: 8px;
  padding: 12px 15px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;

  > .mf-mode__inline-tip-chip {
    align-self: center;
  }

  p {
    margin: 0;
    flex: 1;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.5;
    color: #333;
    text-align: left;
  }
}
.mf-mode__inline-tip-chip {
  flex-shrink: 0;
  background: #f3ae11;
  color: #fff;
  border-radius: 12px;
  padding: 3px 8px;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
  white-space: nowrap;
}

.mf-mode__axis-divider {
  height: 1px;
  background: #ebebeb;
  margin-top: 10px;
}

/* 주변 반응 */
.mf-mode__reaction-card {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.mf-mode__reaction-head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.mf-mode__reaction-who {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 700;
  line-height: 22px;
  color: #333;
}
.mf-mode__reaction-bubble {
  background: #ffeaa7;
  border-radius: 12px;
  padding: 3px 10px;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 16px;
  color: #333;
  position: relative;
}
.mf-mode__reaction-bubble::after {
  content: '';
  position: absolute;
  left: -5px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 6px 7px 6px 0;
  border-color: transparent #ffeaa7 transparent transparent;
}
.mf-mode__reaction-desc {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.5;
  color: #333;

  strong {
    font-weight: 700;
  }
}

/* 생활 리듬 */
.mf-mode__time-card {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.mf-mode__time-head {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 700;
  line-height: 22px;
  color: #333;
}
.mf-mode__time-text p {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.5;
  color: #333;

  strong {
    font-weight: 700;
  }
}

/* 다음 달 예고 */
.mf-mode__next {
  background: #f0f8e8;
  border: 1px dashed #5e9f1c;
  border-radius: 8px;
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.mf-mode__next-title {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  color: #333;
}
.mf-mode__next-text p {
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.5;
  color: #333;
}

/* ===== 기존 placeholder 스타일 ===== */
.mf-drawer__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 28px;
  border-bottom: 1px solid #ebebeb;
  flex-shrink: 0;
}
.mf-drawer__title {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #333;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
}
.mf-drawer__emoji {
  font-size: 24px;
  line-height: 1;
}
.mf-drawer__close {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;

  &:hover {
    background: #f8f8f8;
  }
}
.mf-drawer__close-icon {
  position: relative;
  width: 16px;
  height: 16px;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 1.5px;
    background: #333;
    transform-origin: center;
  }
  &::before {
    transform: translateY(-50%) rotate(45deg);
  }
  &::after {
    transform: translateY(-50%) rotate(-45deg);
  }
}

.mf-drawer__body {
  flex: 1;
  padding: 28px;
}
.mf-drawer__sub {
  margin: 0 0 24px;
  color: #575757;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.mf-drawer__placeholder {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  border-radius: 12px;
  background: #f8f8f8;
  color: #575757;
  font-family: Pretendard;
  font-size: 14px;
  line-height: 20px;

  strong {
    color: #333;
  }
  code {
    background: #fff;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'JetBrains Mono', Consolas, monospace;
    font-size: 13px;
  }
}
.mf-drawer__placeholder-meta {
  margin: 0 0 8px;
  font-size: 13px;
  color: #757575;
}
.mf-drawer__placeholder-block {
  height: 80px;
  border-radius: 8px;
  background: #ebebeb;

  &.short {
    height: 40px;
    width: 60%;
  }
}

/* 트랜지션 — 단일 Transition 안에서 backdrop fade + drawer slide-out 동시 처리 */
.mf-drawer-enter-active,
.mf-drawer-leave-active {
  transition: opacity 0.3s ease;
}
.mf-drawer-enter-from,
.mf-drawer-leave-to {
  opacity: 0;
}

.mf-drawer-enter-active .mf-drawer,
.mf-drawer-leave-active .mf-drawer {
  transition: transform 0.3s ease;
}
.mf-drawer-enter-from .mf-drawer,
.mf-drawer-leave-to .mf-drawer {
  transform: translateX(100%);
}
</style>
